import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, from, switchMap, tap } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { KeycloakAuthService } from 'src/app/auth/keycloak.service';

@Injectable()
export class TokenInterceptorService implements HttpInterceptor {
  constructor(
    private keycloakAuthService: KeycloakAuthService,
    private router: Router
  ) {}

  private isApiRequest(url: string): boolean {
    return url.startsWith(environment.apiUrl);
  }

  private isStaticAsset(url: string): boolean {
    return (
      url.startsWith('assets/') ||
      url.includes('/assets/') ||
      url.endsWith('.svg') ||
      url.endsWith('.png') ||
      url.endsWith('.jpg') ||
      url.endsWith('.jpeg') ||
      url.endsWith('.css') ||
      url.endsWith('.js') ||
      url.endsWith('.ico')
    );
  }

  private isKeycloakRequest(url: string): boolean {
    return url.startsWith(environment.keycloak.url);
  }

  private getPublicFormToken(): string | undefined {
    if (window.location.hash.startsWith('#/public-form/')) {
      return localStorage.getItem('token') ?? undefined;
    }

    return undefined;
  }

  /**
   * Guest endpoints answer 401 as part of their normal flow — a wrong OTP code, or an expired
   * session. Redirecting those to the error page would throw the guest out of the very screen
   * that is meant to recover them.
   */
  private isGuestRequest(url: string): boolean {
    return url.includes('/runtime/guest/');
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isApiRequest(request.url) || this.isStaticAsset(request.url) || this.isKeycloakRequest(request.url)) {
      return next.handle(request);
    }

    return from(this.keycloakAuthService.updateToken(30).catch(() => false)).pipe(
      switchMap(() => {
        const token =
          this.keycloakAuthService.getToken() ?? this.getPublicFormToken();

        const authorisedRequest = token
          ? request.clone({
              setHeaders: {
                Authorization: `Bearer ${token}`
              }
            })
          : request;

        return next.handle(authorisedRequest);
      }),
      tap({
        error: (err: any) => {
          if (err instanceof HttpErrorResponse) {
            if (err.status !== 401 || this.isGuestRequest(request.url)) {
              return;
            }

            this.router.navigate(['error']);
          }
        }
      })
    );
  }
}
